import {
  faBarcode,
  faBoxArchive,
  faCoins,
  faPrint,
  faQrcode,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import _isEmpty from 'lodash/isEmpty';
import PropTypes from 'prop-types';
import React from 'react';
import { Link } from 'react-router-dom';
import TruncatedTitle from 'src/components/TruncatedTitle';
import useActionPanelStore from 'src/stores/useActionPanelStore';
import { BUREAU_BARCODE_FORMAT, LOCALSTORAGE_KEYS } from 'src/utils/constants';
import routes from 'src/utils/routes';
import { getRouteURI, getShortUuid, getUuid } from 'src/utils/url';

import useLocalstorage from '../utils/useLocalStorage';

const ContainerQRCodes = ({
  isBatch,
  printer,
  containers,
  backUri,
  forResource,
  forDisposableContainers,
}) => {
  const { closeActionPanel } = useActionPanelStore();

  const [scanMode] = useLocalstorage(LOCALSTORAGE_KEYS.SCAN_MODE, BUREAU_BARCODE_FORMAT.QR);
  const scanIcon = scanMode === BUREAU_BARCODE_FORMAT.BARCODE ? faBarcode : faQrcode;

  if (_isEmpty(containers) && !printer && !isBatch) {
    return <div>No containers found or all are empty.</div>;
  }

  const renderQrCode = (resource, type) => {
    if (!resource || !type) return null;

    const showUuid = (type === 'container' && resource?.disposable) || isBatch;

    return (
      <div
        key={resource.uri}
        className='container-qr-code'
        data-cy={`container-qr-${getShortUuid(resource.uri)}`}
      >
        <Link
          to={getRouteURI(
            routes.travelerContainers,
            {},
            {
              backUri,
              forResource: resource.uri,
              batch: resource,
              isBatch: isBatch,
            }
          )}
          onClick={closeActionPanel}
        >
          <FontAwesomeIcon icon={scanIcon} className='container-qr-code-icon' />
          {showUuid ? (
            <p className='container-qr-code-text'>
              {isBatch && 'Batch '}
              {getShortUuid(resource.uuid)}
            </p>
          ) : (
            <TruncatedTitle
              title={resource.name}
              maxTextLength={25}
              maxTextLengthMobile={20}
              titleClassName='container-qr-code-text'
            />
          )}
        </Link>
      </div>
    );
  };

  return (
    <div className='qr-codes' data-cy='container-qr-panel'>
      <span className='qr-codes__title' data-cy='container-qr-title'>
        {isBatch ? (
          <>
            <FontAwesomeIcon icon={faBoxArchive} className='spacer-right' />
            Batch:
          </>
        ) : printer ? (
          <>
            <FontAwesomeIcon icon={faPrint} className='spacer-right' />
            Printers:
          </>
        ) : (
          <>
            <FontAwesomeIcon icon={faCoins} className='spacer-right' />
            Containers:
          </>
        )}
      </span>
      <div
        className={classNames(
          'container-qr-codes',
          containers?.length > 2 ? 'justify-content-between' : 'gap30'
        )}
      >
        {containers.length > 1 && (
          <div className='all-containers-active'>
            <Link
              to={getRouteURI(
                routes.travelerContainers,
                {},
                {
                  backUri,
                  forResource,
                  allContainers: true,
                  disposableContainers:
                    forDisposableContainers && containers.map(container => getUuid(container.uri)),
                }
              )}
              data-cy='container-qr-all-link'
              onClick={closeActionPanel}
            >
              <FontAwesomeIcon
                icon={faQrcode}
                className='container-qr-code-icon all-containers-active-icon'
              />
              <p className='container-qr-code-text'>All Containers</p>
            </Link>
          </div>
        )}
        {isBatch
          ? renderQrCode(containers, 'container')
          : printer
            ? renderQrCode(printer, 'printer')
            : containers.map(container => renderQrCode(container, 'container'))}
      </div>
    </div>
  );
};

ContainerQRCodes.defaultProps = {
  containers: [],
  printer: null,
  forDisposableContainers: false,
};

ContainerQRCodes.propTypes = {
  containers: PropTypes.arrayOf(PropTypes.shape({})),
  backUri: PropTypes.string.isRequired,
  forResource: PropTypes.string.isRequired,
  printer: PropTypes.shape({}),
  forDisposableContainers: PropTypes.bool,
  isBatch: PropTypes.bool,
};

export default ContainerQRCodes;
